import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { DamageType } from "../damageType";
import { type RobotActor, RobotActorState, type SomethingWithState } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { deserializeXY } from "../serialization";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import type { Thing } from "../thing";
import { BreakingTier } from "../ai/breakingTier";
import { RobotAI } from "../ai/robotAI";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { XY } from "../xy";
import { placeShrapnelEffectSingle } from "../things/robotUtils";
import { ScrapMetal } from "../things/scrapMetal";
import { ParticleEffect } from "../effects/particleEffect.ts";

export class PlowRobot extends Mob implements SomethingWithState, RobotActor {
	static key = "PlowRobot";
	private state: RobotActorState;
	protected ai: RobotAI;

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(PlowRobot.key);
	}

	constructor(xy: XY, state: RobotActorState = RobotActorState.WANDERING) {
		super(xy);
		this.state = state;
		this.resistsPermanentFreeze = true;
		this.hp = STD_BASE_HP * 4;
		this.maxHp = STD_BASE_HP * 4;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.damageMultipliers.set(DamageType.ELECTRIC, 2.0);
		this.damageMultipliers.set(DamageType.MELEE, 0);
		this.ai = new RobotAI(this);
		this.ai.breakingTier = BreakingTier.ROBOT;
		this.ai.randomWanderMovementProbability = 0.1;
		this.ai.movementVariationProbability = 0.2;
		this.ai.probabilityToBreakBlockWhenStuck = 1.0;
		this.ai.pathfindCostSwitchToHeuristic = 1.7;
		this.ai.attackSoundSupplier = () => new Sound(SoundId.METAL_BANG, this.xy);
		this.ai.noiseSoundSupplier = () => new Sound(SoundId.ROBOT_SOUND, this.xy);
		this.ai.powerOnSoundSupplier = () => new Sound(SoundId.ROBOT_POWER_ON, this.xy);
		this.ai.targetAcquiredSoundSupplier = () => new Sound(SoundId.ROBOT_BIG_TARGET_ACQUIRED, this.xy);
	}

	getState(): RobotActorState {
		return this.state;
	}

	setState(state: RobotActorState): void {
		this.state = state;
	}

	getAttackDamage(): number {
		return Math.floor(STD_BASE_HP * 1.5);
	}

	generateCorpse(): PhysicalThing | null {
		return new ScrapMetal(this.xy);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.ROBOT_BIG_DIE);
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		const damage = super.applyDamage(type, amount, source);
		if (damage > 0 && this.hp === 0) {
			this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy, 0.6));
			this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.LARGE_ROBOT_DEBRIS, 4));
		}
		return damage;
	}

	processMobAction(_board: Board): void {
		this.ai.processTurn();
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), state: this.state, ai: this.ai.serialize() };
	}

	static deserialize(data: any): PlowRobot {
		const m = new PlowRobot(deserializeXY(data.xy), data.state);
		m.restoreMobFields(data);
		m.ai.restoreFrom(data.ai);
		return m;
	}
}
